import { useCallback, useEffect, useRef } from "react"
import { toast } from "@/components/portal/Toast"

/**
 * Liste und geöffnete Mail auf demselben Mandanten halten.
 *
 * Hier liegen zwei Regeln zusammen, die einander in die Quere kamen, solange
 * sie an zwei Stellen standen:
 *
 * **#35 (25.08.2026) — die Liste zieht der Mail nach.** Wird eine Mail über
 * einen Deep-Link geöffnet, die zu einem anderen Betrieb gehört, wechselt die
 * Liste mit. Sonst steht links Reinigungsmandant und rechts Sanitec.
 *
 * **#45 (26.08.2026) — der Nutzer sticht die Mail.** Wechselt jemand selbst den
 * Mandanten, während eine Mail offen ist, gilt sein Wechsel und die Mail wird
 * geschlossen.
 *
 * 🔴 Ohne die Unterscheidung drehte Regel 1 den Wechsel aus Regel 2 sofort
 * zurück: `angleichen` hing per `useCallback` an `[tenant]`, war nach dem
 * Wechsel eine neue Funktionsreferenz, der Effekt in der Detailspalte hatte sie
 * in seinen Abhängigkeiten — und meldete den Mandanten der noch offenen Mail.
 * Paul am 26.08.: „ich kann den tenant nicht wechseln wenn ich in der mail
 * ansicht bin".
 *
 * 🔑 Im Zustand sehen beide Fälle gleich aus — Liste und Mail stehen auf
 * verschiedenen Betrieben. Unterscheidbar sind sie nur daran, **wer** die
 * Änderung ausgelöst hat. Genau das hält `vonUns` fest.
 */
export function useMandantWechsel({
  tenant, setTenant, mailSchliessen,
}: {
  tenant: string
  /** Setzt den globalen Mandanten-Filter (`useTenantFilter`). */
  setTenant: (v: string) => void
  /** Schliesst die offene Mail — in der Liste `setSelectedId(null)`. */
  mailSchliessen: () => void
}) {
  /** Kam der letzte Wechsel von `angleichen` selbst? */
  const vonUns = useRef(false)
  /** Der zuletzt gesehene Mandant — der Effekt darf beim ersten Lauf nichts tun. */
  const vorher = useRef(tenant)

  const angleichen = useCallback((mailTenant: string) => {
    if (!mailTenant || mailTenant === tenant) return
    vonUns.current = true
    setTenant(mailTenant)
    toast(`Mandant gewechselt — die Mail gehoert zu ${mailTenant}`, "success")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant])

  useEffect(() => {
    // Erster Lauf: nichts tun. Sonst würfe ein Deep-Link (`/emails?open=123`)
    // die gerade geöffnete Mail sofort wieder weg.
    if (vorher.current === tenant) return
    vorher.current = tenant
    // Der Wechsel kam von uns selbst (#35) — dann ist die offene Mail der
    // Grund dafür und bleibt stehen. `vonUns` gilt für genau diesen einen
    // Wechsel; der nächste schliesst wieder.
    if (vonUns.current) {
      vonUns.current = false
      return
    }
    mailSchliessen()
    // `mailSchliessen` bewusst NICHT in den Abhängigkeiten: der Aufrufer gibt
    // es als Inline-Funktion mit, also bei jedem Render eine neue Referenz —
    // und dann liefe der Effekt bei jedem Render statt bei jedem Wechsel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant])

  return angleichen
}
